import { Component, OnInit, ElementRef, ViewChild, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { DispatchService } from '../service/dispatch.service';
import { ZoneService } from '../service/zone.service';
import { CognitoService } from '../service/cognito.service';
import { ThemeService } from '../service/theme.service';
import { BinUsage } from '../constants/bin-usage';
import { IotService, DeviceConfig } from '../service/iot.service';
import { ReportsService } from '../service/reports.service';
import { DashboardService } from '../service/dashboard.service';
import { ClientService } from '../service/client.service';
import { SystemMessageService } from '../service/system-message.service';

type LatLng = { lat: number; lng: number };

interface ZoneOption {
  zone_id: string;
  zone_name: string;
  zone_color: string;
  zone_coordinates: LatLng[];
  active?: string;
  waste_type?: string;
}

interface DeviceTile {
  thing_name: string;
  bin_id?: string;
  bin_name?: string;
  bin_usage?: string;
  bin_address?: string;
  bin_gps?: string;
  bin_location?: string;
  client_id?: string;
  selected: boolean;
  zone_id?: string | null;
}

@Component({
  selector: 'app-dispatch-zones',
  templateUrl: './dispatch-zones.component.html',
  styleUrls: ['./dispatch-zones.component.css'],
  standalone: false
})
export class DispatchZonesComponent implements OnInit {
  @ViewChild('zonesMap') zonesMapEl?: ElementRef<HTMLDivElement>;
  public loading = true;
  public hasError = false;

  public operatorId = '';
  public devices: DeviceTile[] = [];
  public groupedDevices: { usage: string; devices: DeviceTile[] }[] = [];
  public zones: ZoneOption[] = [];
  public zoneDeviceMap: Record<string, string> = {};
  private zoneNameById = new Map<string, string>();
  public clients: { client_id: string; client_name: string }[] = [];
  public selectedClientId = '';

  public showAddToZoneModal = false;
  public showCreateZoneModal = false;
  public showZonesMapModal = false;
  public showDeleteZoneModal = false;
  public showTextOverlay = false;
  public overlayText = '';
  public overlayTitle = '';
  public selectedZoneIds: Set<string> = new Set();
  private ribbonTimeoutId: number | null = null;

  public selectedZoneId = '';
  public newZoneName = '';
  public newZoneColor = '#22c55e';
  public newWasteType = '';
  public newZoneActive = '1';

  public mapCenter: LatLng = { lat: 45.5017, lng: -73.5673 };
  public mapZoom = 9;
  private mapInstance: google.maps.Map | null = null;
  private mapPolygons: google.maps.Polygon[] = [];
  private mapMarkers: google.maps.Marker[] = [];
  private geocoder: google.maps.Geocoder | null = null;
  private geocodeCache = new Map<string, LatLng>();

  public binUsage = BinUsage;
  public startDate: any = this.reports.getFirstDateOfTheCurrentYear();
  public endDate: any = this.reports.getCurrentDate();

  constructor(
    private dispatchService: DispatchService,
    private zoneService: ZoneService,
    private cognitoService: CognitoService,
    public theme: ThemeService,
    public iotService: IotService,
    public reports: ReportsService,
    public dashboardService: DashboardService,
    private clientService: ClientService,
    public systemMessage: SystemMessageService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Loads the zone workspace data needed by the list, actions, and map modal.
   */
  async ngOnInit() {
    this.setPageLoading(true);
    await this.cognitoService.getUser();
    this.operatorId = this.cognitoService.userInfos.attributes['custom:operations_id'] || '';

    // Load the independent datasets in parallel, then reconcile device-to-zone state.
    await Promise.all([
      this.loadZones(),
      this.loadDevices(),
      this.loadZoneDevices(),
      this.loadClients()
    ]);

    this.applyZoneAssignments();
    this.loading = false;
    this.setPageLoading(false);
  }

  get selectedDevices(): DeviceTile[] {
    return this.devices.filter((d) => d.selected);
  }

  /**
   * A device can only participate in zoning if it has an address we can map.
   */
  isDeviceSelectable(device: DeviceTile): boolean {
    const hasAddress = !!(device.bin_address && device.bin_address.trim().length);
    return hasAddress;
  }

  /**
   * Applies the current client filter to the loaded device list.
   */
  get filteredDevices(): DeviceTile[] {
    if (!this.selectedClientId) {
      return this.devices;
    }
    return this.devices.filter((d) => d.client_id === this.selectedClientId);
  }

  /**
   * Returns the number of zones currently loaded for the operator.
   */
  get totalZoneCount(): number {
    return this.zones.length;
  }

  /**
   * Counts how many devices are currently assigned to a given zone.
   */
  getZoneDeviceCount(zoneId: string): number {
    return this.devices.filter((d) => d.zone_id === zoneId).length;
  }

  /**
   * Resolves a zone id to its display name for tile badges and dialogs.
   */
  getZoneName(zoneId: string | null | undefined): string {
    if (!zoneId) {
      return '';
    }
    return this.zoneNameById.get(zoneId) || '';
  }

  /**
   * Rebuilds grouped device sections after the client filter changes.
   */
  onClientFilterChange() {
    this.groupDevices();
  }

  /**
   * Toggles a device selection unless the device is missing an address.
   */
  toggleSelection(device: DeviceTile) {
    if (!this.isDeviceSelectable(device)) {
      return;
    }
    device.selected = !device.selected;
  }

  /**
   * Clears all currently selected devices.
   */
  clearSelection() {
    this.devices.forEach((d) => (d.selected = false));
  }

  /**
   * Opens the zone assignment modal for either one device or the current multi-selection.
   */
  async openAddToZoneModal(device?: DeviceTile) {
    if (device) {
      this.devices.forEach((d) => (d.selected = d.thing_name === device.thing_name));
    }
    const selectionZoneId = this.getSelectionZoneId();
    this.selectedZoneId = selectionZoneId || '';
    await this.loadZones();
    this.showAddToZoneModal = true;
  }

  /**
   * Resets zone form state and opens the create-zone modal.
   */
  openCreateZoneModal() {
    this.newZoneName = '';
    this.newWasteType = '';
    this.newZoneColor = '#22c55e';
    this.newZoneActive = '1';
    this.showCreateZoneModal = true;
  }

  /**
   * Opens the bulk-delete dialog with a fresh zone selection set.
   */
  openDeleteZoneModal() {
    this.selectedZoneIds = new Set();
    this.showDeleteZoneModal = true;
  }

  /**
   * Opens the map modal and defers map creation until the container is rendered.
   */
  openZonesMapModal() {
    this.showZonesMapModal = true;
    setTimeout(() => this.renderZonesMap(), 0);
  }

  /**
   * Closes every zone-related modal managed by this component.
   */
  closeModals() {
    this.showAddToZoneModal = false;
    this.showCreateZoneModal = false;
    this.showZonesMapModal = false;
    this.showDeleteZoneModal = false;
  }

  /**
   * Displays a simple read-only overlay for long device text values.
   */
  openTextOverlay(title: string, text: string) {
    if (!text) {
      return;
    }
    this.overlayTitle = title;
    this.overlayText = text;
    this.showTextOverlay = true;
  }

  /**
   * Closes the text overlay and clears its content.
   */
  closeTextOverlay() {
    this.showTextOverlay = false;
    this.overlayText = '';
    this.overlayTitle = '';
  }

  /**
   * Locks body scrolling while a full-page loading state is active.
   */
  private setPageLoading(isLoading: boolean) {
    if (isLoading) {
      this.renderer.addClass(this.document.body, 'no-scroll');
    } else {
      this.renderer.removeClass(this.document.body, 'no-scroll');
    }
  }

  /**
   * Shows a transient ribbon notification and replaces any in-flight timeout.
   */
  private showRibbon(type: string, messageKey: string) {
    this.systemMessage.selectRibbon(type, messageKey);
    if (this.ribbonTimeoutId !== null) {
      clearTimeout(this.ribbonTimeoutId);
    }
    this.ribbonTimeoutId = window.setTimeout(() => {
      this.systemMessage.closeRibbon();
      this.ribbonTimeoutId = null;
    }, 2000);
  }

  /**
   * Loads the operator's devices and derives the tile data used by the zones UI.
   */
  async loadDevices() {
    try {
      const deviceRows = await firstValueFrom(this.dispatchService.getAllThings());
      const things = (deviceRows || []).map((d) => d.thing_name).filter(Boolean);
      if (things.length === 0) {
        this.devices = [];
        this.groupedDevices = [];
        return;
      }

      await this.loadDevicesFromHealth(things);
      this.applyZoneAssignments();
    } catch (error) {
      console.error('Failed to load devices', error);
      this.hasError = true;
    }
  }

  /**
   * Joins health, config, shadow, and bin data into a normalized device tile model.
   */
  async loadDevicesFromHealth(things: string[]) {
    const start = this.dateToUnixTimestamp(this.startDate);
    const end = this.dateToUnixTimestamp(this.endDate + 'T23:59:59');

    const data: any = await this.iotService.getDeviceHealth(things);
    const result: any = await this.reports.getReportData(things, start, end, false);

    const thingStatusArray = JSON.parse(data.status || '[]');
    const configs: DeviceConfig[] = JSON.parse(data.configs || '[]');
    const bins: any[] = JSON.parse(data.bins || '[]');
    const shadows: any[] = JSON.parse(data.shadows || '[]');

    // Mirror the dispatch-network data assembly so both views stay aligned.
    this.devices = thingStatusArray.map((filteredItem: any) => {
      const config = configs.filter(
        (item: DeviceConfig) => item.thing_name === filteredItem.thing_name
      );
      const binRows = bins.filter((item: any) => item.thing_name === filteredItem.thing_name);
      const [bin] = binRows || [];
      const shadowRows = shadows.filter((item: any) => item.thing_name === filteredItem.thing_name);
      const [shadow] = shadowRows || [];

      const device = this.dashboardService.getDevicesStructure(
        shadow,
        bin,
        config
      );

      return {
        thing_name: filteredItem.thing_name,
        bin_id: bin?.bin_id,
        bin_name: bin?.bin_name,
        bin_usage: bin?.bin_usage,
        bin_address: bin?.bin_address,
        bin_gps: bin?.bin_gps,
        bin_location: bin?.bin_location,
        client_id: bin?.client_id,
        selected: false,
        zone_id: bin?.zone_id || null,
        device
      } as DeviceTile;
    });

    this.groupDevices();
    void result; // preserve same report pipeline as dispatch-network
  }

  /**
   * Converts an ISO-like date string into a Unix timestamp in seconds.
   */
  dateToUnixTimestamp(dateString: string): number {
    const date = new Date(dateString);
    const timestampInMilliseconds = date.getTime();
    return Math.floor(timestampInMilliseconds / 1000);
  }

  /**
   * Formats a date as `YYYYMMDD` for the zone service payloads.
   */
  transformDateTo8Digits(inputDate: Date): number {
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth() + 1;
    const day = inputDate.getDate();
    const formattedMonth = month < 10 ? `0${month}` : `${month}`;
    const formattedDay = day < 10 ? `0${day}` : `${day}`;
    return parseInt(`${year}${formattedMonth}${formattedDay}`, 10);
  }

  /**
   * Loads all zones owned by the current operator.
   */
  async loadZones() {
    if (!this.operatorId) {
      return;
    }
    try {
      const response: any = await firstValueFrom(this.zoneService.getOperatorZones(this.operatorId));
      const rows = Array.isArray(response) ? response : [];
      this.zones = rows.map((item: any) => {
        const coordinates = this.parseZoneCoordinates(item.zone_coordinates);
        return {
          zone_id: item.zone_id,
          zone_name: item.zone_name,
          zone_color: item.zone_color,
          zone_coordinates: coordinates,
          active: item.active,
          waste_type: item.waste_type
        };
      });
      this.zoneNameById = new Map(
        this.zones.map((zone) => [zone.zone_id, zone.zone_name])
      );
    } catch (error) {
      console.error('Failed to load zones', error);
      this.hasError = true;
    }
  }

  /**
   * Loads the persisted device-to-zone assignments for the current operator.
   */
  async loadZoneDevices() {
    if (!this.operatorId) {
      return;
    }
    try {
      const response: any = await firstValueFrom(this.zoneService.getOperatorZoneDevices(this.operatorId));
      const rows = Array.isArray(response) ? response : [];
      this.zoneDeviceMap = rows.reduce((acc: Record<string, string>, row: any) => {
        if (row.thing_name && row.zone_id) {
          acc[row.thing_name] = row.zone_id;
        }
        return acc;
      }, {});
    } catch (error) {
      console.error('Failed to load zone devices', error);
      this.hasError = true;
    }
  }

  /**
   * Loads client options for the device filter dropdown.
   */
  async loadClients() {
    try {
      const clients = await this.clientService.fetchClients();
      const valid = Array.isArray(clients) ? clients : [];
      this.clients = valid
        .filter((client) => client.client_id && client.client_name)
        .map((client) => ({
          client_id: client.client_id as string,
          client_name: client.client_name as string
        }));
    } catch (error) {
      console.error('Failed to load clients', error);
    }
  }

  /**
   * Groups visible devices by waste usage so the list renders in sections.
   */
  groupDevices() {
    const groups = new Map<string, DeviceTile[]>();
    this.filteredDevices.forEach((device) => {
      const key = device.bin_usage || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(device);
    });

    // Keep unknown usage buckets last so mapped waste streams stay prominent.
    this.groupedDevices = Array.from(groups.entries()).map(([usage, devices]) => ({
      usage,
      devices
    })).sort((a, b) => {
      const aUnknown = a.usage === 'unknown';
      const bUnknown = b.usage === 'unknown';
      if (aUnknown && !bUnknown) {
        return 1;
      }
      if (!aUnknown && bUnknown) {
        return -1;
      }
      return a.usage.localeCompare(b.usage);
    });
  }

  /**
   * Applies the latest zone-device mapping onto the in-memory device list.
   */
  applyZoneAssignments() {
    if (!this.devices.length) {
      return;
    }
    this.devices.forEach((device) => {
      if (this.zoneDeviceMap[device.thing_name]) {
        device.zone_id = this.zoneDeviceMap[device.thing_name];
      }
    });
  }

  /**
   * Assigns the current device selection to an existing zone and refreshes local state.
   */
  async addSelectedToZone() {
    if (!this.selectedZoneId) {
      return;
    }

    const selected = this.selectedDevices;
    const thingNames = selected.map((device) => device.thing_name);
    const created = this.transformDateTo8Digits(new Date()).toString();
    const zoneCoordinates = await this.buildZoneRectangleFor(this.selectedZoneId, selected);
    const modified = zoneCoordinates ? this.transformDateTo8Digits(new Date()).toString() : undefined;
    try {
      await firstValueFrom(
        this.zoneService.setOperatorZoneDevices(
          this.selectedZoneId,
          thingNames,
          created,
          this.operatorId,
          this.newZoneActive,
          zoneCoordinates || undefined,
          modified
        )
      );
      selected.forEach((device) => {
        device.zone_id = this.selectedZoneId;
      });
      this.showRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      console.error('Failed to update zone devices', error);
      this.hasError = true;
      this.showRibbon('danger', 'alert-danger-generic-message');
    } finally {
      this.closeModals();
      this.clearSelection();
    }
  }

  /**
   * Creates a new zone around the selected devices, then refreshes zone state.
   */
  async createZoneFromSelection() {
    const selected = this.selectedDevices;
    if (!this.newZoneName) {
      return;
    }

    const points = await this.getUniquePointsAsync(selected);
    if (points.length < 1) {
      return;
    }

    // Build the smallest buffered rectangle that covers the selected device points.
    const polygon = this.buildMinAreaRectangle(points);

    this.zoneService.zoneData.owner_id = this.operatorId;
    this.zoneService.zoneData.zone_name = this.newZoneName;
    this.zoneService.zoneData.zone_color = this.newZoneColor;
    this.zoneService.zoneData.active = this.newZoneActive;
    this.zoneService.zoneData.waste_type = this.newWasteType;
    this.zoneService.zoneData.zone_coordinates = JSON.stringify(polygon);
    this.zoneService.zoneData.created = this.transformDateTo8Digits(new Date()).toString();
    this.zoneService.zoneData.created_by = this.operatorId;
    this.zoneService.zoneData.thing_names = selected.map((d) => d.thing_name);

    try {
      await firstValueFrom(this.zoneService.createOperatorZone());
      await Promise.all([this.loadZones(), this.loadZoneDevices()]);
      this.applyZoneAssignments();
      this.groupDevices();
      if (this.showZonesMapModal) {
        this.renderZonesMap();
      }
      this.showRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      console.error('Failed to create zone', error);
      this.hasError = true;
      this.showRibbon('danger', 'alert-danger-generic-message');
    }

    this.closeModals();
    this.clearSelection();
  }

  /**
   * Removes a single device from its zone and recomputes that zone geometry.
   */
  async removeFromZone(device: DeviceTile) {
    if (!device.zone_id) {
      return;
    }
    const zoneId = device.zone_id;
    try {
      await firstValueFrom(this.zoneService.removeOperatorZoneDevices([device.thing_name]));
      device.zone_id = null;
      await this.updateZonePolygon(zoneId);
      if (this.showZonesMapModal) {
        this.renderZonesMap();
      }
      this.showRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      console.error('Failed to remove device from zone', error);
      this.hasError = true;
      this.showRibbon('danger', 'alert-danger-generic-message');
    }
  }

  /**
   * Toggles a zone inside the bulk-delete selection set.
   */
  toggleZoneSelection(zoneId: string, checked: boolean) {
    if (checked) {
      this.selectedZoneIds.add(zoneId);
    } else {
      this.selectedZoneIds.delete(zoneId);
    }
  }

  /**
   * Deletes all selected zones and clears local assignments for affected devices.
   */
  async deleteSelectedZones() {
    if (this.selectedZoneIds.size === 0) {
      return;
    }

    const zoneIds = Array.from(this.selectedZoneIds);
    try {
      for (const zoneId of zoneIds) {
        await firstValueFrom(this.zoneService.deleteOperatorZone(zoneId));
      }
      this.devices.forEach((device) => {
        if (device.zone_id && this.selectedZoneIds.has(device.zone_id)) {
          device.zone_id = null;
        }
      });
      await Promise.all([this.loadZones(), this.loadZoneDevices()]);
      this.applyZoneAssignments();
      this.groupDevices();
      if (this.showZonesMapModal) {
        this.renderZonesMap();
      }
      this.showRibbon('success', 'alert-success-generic-message');
    } catch (error) {
      console.error('Failed to delete zones', error);
      this.hasError = true;
      this.showRibbon('danger', 'alert-danger-generic-message');
    } finally {
      this.closeModals();
      this.selectedZoneIds = new Set();
    }
  }

  /**
   * Rebuilds and persists the polygon for a zone after membership changes.
   */
  private async updateZonePolygon(zoneId: string) {
    const zone = this.zones.find((z) => z.zone_id === zoneId);
    if (!zone) {
      return;
    }

    const zoneCoordinates = await this.buildZoneRectangleFor(zoneId);
    if (!zoneCoordinates) {
      return;
    }

    this.zoneService.zoneData.zone_id = zone.zone_id;
    this.zoneService.zoneData.zone_coordinates = zoneCoordinates;
    this.zoneService.zoneData.zone_name = zone.zone_name;
    this.zoneService.zoneData.modified = this.transformDateTo8Digits(new Date()).toString();
    this.zoneService.zoneData.active = zone.active || '1';
    this.zoneService.zoneData.zone_color = zone.zone_color;
    this.zoneService.zoneData.waste_type = zone.waste_type || '';

    try {
      await firstValueFrom(this.zoneService.updateOperatorZone());
      if (this.showZonesMapModal) {
        this.renderZonesMap();
      }
    } catch (error) {
      console.error('Failed to update zone polygon', error);
      this.hasError = true;
    }
  }

  /**
   * Builds a buffered rectangle from the devices already in a zone plus any staged additions.
   */
  private async buildZoneRectangleFor(zoneId: string, extraDevices: DeviceTile[] = []): Promise<string | null> {
    const deviceMap = new Map<string, DeviceTile>();
    this.devices.forEach((device) => {
      if (device.zone_id === zoneId) {
        deviceMap.set(device.thing_name, device);
      }
    });
    extraDevices.forEach((device) => deviceMap.set(device.thing_name, device));

    const points = await this.getUniquePointsAsync(Array.from(deviceMap.values()));
    if (points.length < 1) {
      return null;
    }

    const polygon = this.buildMinAreaRectangle(points);
    return JSON.stringify(polygon);
  }

  /**
   * Collects unique coordinate points for every device currently assigned to a zone.
   */
  private async getUniqueZonePoints(zoneId: string): Promise<LatLng[]> {
    const devices = this.devices.filter((device) => device.zone_id === zoneId);
    return this.getUniquePointsAsync(devices);
  }

  /**
   * Resolves devices to coordinates and removes duplicates before polygon generation.
   */
  private async getUniquePointsAsync(devices: DeviceTile[]): Promise<LatLng[]> {
    const points = await Promise.all(
      devices.map(async (device) => this.getDeviceCoords(device))
    );

    const unique = new Map<string, LatLng>();
    // Round the key to avoid near-identical float noise producing duplicate vertices.
    points.forEach((p) => {
      if (!p) {
        return;
      }
      const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
      if (!unique.has(key)) {
        unique.set(key, p);
      }
    });

    return Array.from(unique.values());
  }

  /**
   * Lazily creates a Google geocoder instance when the Maps API is available.
   */
  private getGeocoder(): google.maps.Geocoder | null {
    if (this.geocoder) {
      return this.geocoder;
    }
    const g = (window as any).google?.maps;
    if (!g?.Geocoder) {
      return null;
    }
    this.geocoder = new g.Geocoder();
    return this.geocoder;
  }

  /**
   * Resolves coordinates from GPS first, then falls back to geocoding the device address.
   */
  private async getDeviceCoords(device: DeviceTile): Promise<LatLng | null> {
    const direct = this.parseCoords(device);
    if (direct) {
      return direct;
    }
    const address = device.bin_address?.trim();
    if (!address) {
      return null;
    }
    const key = address.toLowerCase();
    // Cache geocoding results because zone generation can revisit the same address repeatedly.
    if (this.geocodeCache.has(key)) {
      return this.geocodeCache.get(key) || null;
    }
    const geocoder = this.getGeocoder();
    if (!geocoder) {
      return null;
    }
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          const coords = { lat: loc.lat(), lng: loc.lng() };
          this.geocodeCache.set(key, coords);
          resolve(coords);
        } else {
          resolve(null);
        }
      });
    });
  }

  /**
   * Parses a `lat,lng` string from device GPS or location fields.
   */
  parseCoords(device: DeviceTile): LatLng | null {
    const raw = device.bin_gps || device.bin_location || '';
    if (!raw || !raw.includes(',')) {
      return null;
    }
    const [latRaw, lngRaw] = raw.split(',').map((v) => v.trim());
    const lat = Number(latRaw);
    const lng = Number(lngRaw);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  /**
   * Builds a polygon from points, using a hull only when there are enough points to need one.
   */
  buildPolygon(points: LatLng[]): LatLng[] {
    if (points.length <= 3) {
      return points;
    }
    return this.convexHull(points);
  }

  /**
   * Builds a buffered minimum-area rectangle around a set of points.
   */
  buildMinAreaRectangle(points: LatLng[], bufferMeters = 2000): LatLng[] {
    if (points.length === 0) {
      return [];
    }

    if (points.length === 1) {
      const centroid = points[0];
      const rect = [
        { x: -bufferMeters, y: -bufferMeters },
        { x: bufferMeters, y: -bufferMeters },
        { x: bufferMeters, y: bufferMeters },
        { x: -bufferMeters, y: bufferMeters },
      ];
      return rect.map((p) => this.metersToLatLng(p, centroid));
    }

    const hull = this.convexHull(points);
    const usePoints = hull.length >= 3 ? hull : points;

    const centroid = this.getCentroid(usePoints);
    const projected = usePoints.map((p) => this.latLngToMeters(p, centroid));

    let bestArea = Number.POSITIVE_INFINITY;
    let best = { angle: 0, minX: 0, maxX: 0, minY: 0, maxY: 0 };

    if (usePoints.length >= 3) {
      // Evaluate each hull edge as a candidate rectangle orientation.
      for (let i = 0; i < projected.length; i++) {
        const a = projected[i];
        const b = projected[(i + 1) % projected.length];
        const angle = Math.atan2(b.y - a.y, b.x - a.x);

        const { minX, maxX, minY, maxY } = this.getBoundsAfterRotation(projected, -angle);
        const area = (maxX - minX) * (maxY - minY);

        if (area < bestArea) {
          bestArea = area;
          best = { angle, minX, maxX, minY, maxY };
        }
      }
    } else {
      // Collinear or near-collinear points: align rectangle with the longest segment.
      const { a, b } = this.getFarthestPair(projected);
      const angle = Math.atan2(b.y - a.y, b.x - a.x);
      const { minX, maxX, minY, maxY } = this.getBoundsAfterRotation(projected, -angle);
      best = { angle, minX, maxX, minY, maxY };
    }

    const minX = best.minX - bufferMeters;
    const maxX = best.maxX + bufferMeters;
    const minY = best.minY - bufferMeters;
    const maxY = best.maxY + bufferMeters;

    const rect = [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ].map((p) => this.rotatePoint(p, best.angle));

    return rect.map((p) => this.metersToLatLng(p, centroid));
  }

  /**
   * Computes the convex hull of a point set using the monotonic chain algorithm.
   */
  convexHull(points: LatLng[]): LatLng[] {
    const sorted = [...points].sort((a, b) => (a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng));

    const cross = (o: LatLng, a: LatLng, b: LatLng) =>
      (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);

    const lower: LatLng[] = [];
    for (const p of sorted) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
        lower.pop();
      }
      lower.push(p);
    }

    const upper: LatLng[] = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
        upper.pop();
      }
      upper.push(p);
    }

    upper.pop();
    lower.pop();
    return lower.concat(upper);
  }

  /**
   * Returns the centroid of a set of latitude/longitude points.
   */
  private getCentroid(points: LatLng[]): LatLng {
    const sum = points.reduce(
      (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / points.length, lng: sum.lng / points.length };
  }

  /**
   * Projects a lat/lng point to a local meter-based plane around an origin.
   */
  private latLngToMeters(point: LatLng, origin: LatLng): { x: number; y: number } {
    const R = 6371000;
    const lat0 = (origin.lat * Math.PI) / 180;
    const dLat = ((point.lat - origin.lat) * Math.PI) / 180;
    const dLng = ((point.lng - origin.lng) * Math.PI) / 180;
    return {
      x: R * dLng * Math.cos(lat0),
      y: R * dLat,
    };
  }

  /**
   * Converts a local meter-based point back into latitude/longitude.
   */
  private metersToLatLng(point: { x: number; y: number }, origin: LatLng): LatLng {
    const R = 6371000;
    const lat0 = (origin.lat * Math.PI) / 180;
    const dLat = point.y / R;
    const dLng = point.x / (R * Math.cos(lat0));
    return {
      lat: origin.lat + (dLat * 180) / Math.PI,
      lng: origin.lng + (dLng * 180) / Math.PI,
    };
  }

  /**
   * Rotates a 2D point around the origin by the provided angle in radians.
   */
  private rotatePoint(point: { x: number; y: number }, angle: number): { x: number; y: number } {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: point.x * cos - point.y * sin,
      y: point.x * sin + point.y * cos,
    };
  }

  /**
   * Measures the axis-aligned bounds after rotating a point set.
   */
  private getBoundsAfterRotation(points: { x: number; y: number }[], angle: number) {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    points.forEach((p) => {
      const r = this.rotatePoint(p, angle);
      minX = Math.min(minX, r.x);
      maxX = Math.max(maxX, r.x);
      minY = Math.min(minY, r.y);
      maxY = Math.max(maxY, r.y);
    });

    return { minX, maxX, minY, maxY };
  }

  /**
   * Finds the farthest pair in a projected point set for collinear rectangle alignment.
   */
  private getFarthestPair(points: { x: number; y: number }[]) {
    let maxDist = -1;
    let pair = { a: points[0], b: points[0] };
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const dx = points[j].x - points[i].x;
        const dy = points[j].y - points[i].y;
        const dist = dx * dx + dy * dy;
        if (dist > maxDist) {
          maxDist = dist;
          pair = { a: points[i], b: points[j] };
        }
      }
    }
    return pair;
  }

  /**
   * Builds map-ready polygons for all zones that currently resolve to at least one point.
   */
  async getZonePolygons(): Promise<{ paths: LatLng[]; strokeColor: string; fillColor: string }[]> {
    try {
      const items = await Promise.all(
        this.zones.map(async (zone) => {
          const points = await this.getUniqueZonePoints(zone.zone_id);
          if (points.length < 1) {
            return null;
          }

          return {
            paths: this.buildMinAreaRectangle(points),
            strokeColor: zone.zone_color,
            fillColor: zone.zone_color
          };
        })
      );
      return items.filter((poly): poly is { paths: LatLng[]; strokeColor: string; fillColor: string } => !!poly);
    } catch (error) {
      console.error('Failed to build zone polygons', error);
      return [];
    }
  }

  /**
   * Returns a shared zone id only when every selected device belongs to the same zone.
   */
  getSelectionZoneId(): string | null {
    const selected = this.selectedDevices;
    if (selected.length === 0) {
      return null;
    }
    const zoneId = selected[0].zone_id || null;
    const same = selected.every((d) => (d.zone_id || null) === zoneId);
    return same ? zoneId : null;
  }

  /**
   * Parses stored zone coordinate payloads, tolerating legacy string formats.
   */
  parseZoneCoordinates(raw: any): LatLng[] {
    if (!raw) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw as LatLng[];
    }
    if (typeof raw !== 'string') {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      try {
        const normalized = raw
          .replace(/'/g, '"')
          .replace(/([,{]\s*)(lat|lng)\s*:/g, '$1"$2":');
        const parsed = JSON.parse(normalized);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Invalid zone_coordinates format', error);
        return [];
      }
    }
  }

  /**
   * Recreates the Google map, then draws current zone polygons and device markers.
   */
  private async renderZonesMap() {
    if (!this.zonesMapEl?.nativeElement) {
      return;
    }
    if (!(window as any).google?.maps) {
      console.error('Google Maps is not available');
      return;
    }
    const map = new google.maps.Map(this.zonesMapEl.nativeElement, {
      center: this.mapCenter,
      zoom: this.mapZoom,
      disableDefaultUI: true,
    });
    this.mapInstance = map;
    this.clearMapPolygons();
    this.clearMapMarkers();

    const polygons = await this.getZonePolygons();
    polygons.forEach((poly) => {
      const polygon = new google.maps.Polygon({
        paths: poly.paths,
        strokeColor: poly.strokeColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: poly.fillColor,
        fillOpacity: 0.2,
        editable: false,
      });
      polygon.setMap(map);
      this.mapPolygons.push(polygon);
    });

    const markers = await this.getDeviceMarkers();
    markers.forEach((marker) => marker.setMap(map));
  }

  /**
   * Removes all rendered zone polygons from the active map instance.
   */
  private clearMapPolygons() {
    this.mapPolygons.forEach((poly) => poly.setMap(null));
    this.mapPolygons = [];
  }

  /**
   * Removes all rendered device markers from the active map instance.
   */
  private clearMapMarkers() {
    this.mapMarkers.forEach((marker) => marker.setMap(null));
    this.mapMarkers = [];
  }

  /**
   * Builds map markers for every device that resolves to a coordinate.
   */
  private async getDeviceMarkers(): Promise<google.maps.Marker[]> {
    const markers: google.maps.Marker[] = [];
    for (const device of this.devices) {
      const coords = await this.getDeviceCoords(device);
      if (!coords) {
        continue;
      }
      markers.push(new google.maps.Marker({
        position: coords,
        title: device.thing_name,
        opacity: 0.85,
      }));
    }
    this.mapMarkers = markers;
    return markers;
  }
}
